<template>
  <div>
    <v-client-table :data="data.echeanceAPayerFaces"
      class='mgt-10px is-paddingless'
      :columns="columns"
      :options="options">
      <template #selected="{ row }">
        <qd-checkbox
          :value="row.selected"
          @input="$emit('actionSelected', { id: row.id, selected: row.selected })"
          class="has-text-centered">
        </qd-checkbox>
      </template>
      <template #codeFace = "{ row }">
        <router-link-blank
          v-if="getProduitRouting(row)"
          v-bind="getProduitRouting(row)">
        </router-link-blank>
      </template>
      <template #montantEcheanceHt = "{ row }">
        {{ func.formatDevise(row.montantEcheanceHt) }}
      </template>
      <template #montantEcheanceTtc = "{ row }">
        {{ func.formatDevise(row.montantEcheanceTtc) }}
      </template>
      <template #referencePaiement = "{ row }">
        <div class="has-text-editInTable">
          {{ row.referencePaiement }}
        </div>
      </template>
      <template #bonAPayer = "{ row }" >
        <div class="has-text-editInTable">
          {{ row.bonAPayer ? $t('general.oui') : $t('general.non') }}
        </div>
      </template>
      <template #commentaireLigneCommande = "{ row }" >
        <div class="has-text-editInTable ellipsis">
          {{ row.commentaireLigneCommande }}
        </div>
      </template>
      <template #validateur = "{ row }" >
        <div class="has-text-editInTable">
          {{ findLibelleValidateur(row.validateur) }}
        </div>
      </template>
      <template #affectation = "{ row }" >
        <router-link-blank v-if="row.affectations && row.affectations.length === 1"
          :to="{ name: (getInformationDirection(row.affectations[0])).routeName,
            params: { id: (getInformationDirection(row.affectations[0])).id }}"
          :message="(getLibelleOfAffectation(row.affectations[0]))">
        </router-link-blank>
        <p v-else-if="row.affectations && row.affectations.length > 1"
          class="lien is-cursor-pointer"
          @click="saveInformationAndOpenPopUp(row)">
          {{ $t('listeFace.tab.multiple') }}
        </p>
      </template>
      <template #edit = "{ row }">
        <div class="has-text-centered">
          <btn-action-icon
            :permission="constantes.ECHEANCIER_CONTRAT.maj"
            @is-click="$emit('editEcheance', row)"
            icon-type="is-success"
            icon="pencil-box-outline">
          </btn-action-icon>
        </div>
      </template>
      <template #actions = "{ row }">
        <verticalDotsDropdown
          :dropdownItems="dropdownItems(row)"
          :isButton="false"/>
      </template>
    </v-client-table>
    <b-modal v-model="popUpAffectationsFace"
      has-modal-card>
      <pop-up-info-affectations-face
        v-if="produitInformation"
        :affectations="produitInformation.affectations"
        :isFromLigneFace="false"
        :isLogistique="false"
        :traduction="{
          title: $t('listeEcheancesAPayer.infoAffectation.title',
          { code: produitInformation.codeFace }),
          noAffectation: $t('listeEcheancesAPayer.infoAffectation.noAffectation')}">
      </pop-up-info-affectations-face>
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import {
  NO_PER_PAGE_VALUE, RESULTS_PER_POP_UP, SORT_ICON, TEXTS,
} from '@/configuration/clientTableConf';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import { PARAMETRES_INTERNE_KEYS, PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import {
  AFFECTATION_RESEAU,
  CATEGORIE_REGROUPEMENT,
  CATEGORIE_PRODUIT,
} from '@/configuration/catalogue/produitConf';
import { TYPE_COMMANDE, TYPE_DEVIS } from '@/configuration/devisConf';
import { COMMANDER } from '@/configuration/affectation/affectationConf';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';
import formatDate from '@/services/general/formatIsoDate';
import { formatDevise } from '@/services/general/filters/formatDevise';
import popUpInfoAffectationsFace from '@/components/general/p_infoAffectationsFace';
import { ECHEANCIER_CONTRAT } from '@/configuration/baux/permissionsBauxConf';
import BtnActionIcon from '@/components/general/btnActionIcon';
import verticalDotsDropdown from '@/components/general/button/verticalDotsDropdown';

const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'childRowEcheanceAPAyerFace',
  props: {
    data: {
      type: Object,
      required: true,
      default: null,
    },
  },
  data() {
    return {
      func: {
        formatDevise,
      },
      colonneSortExclusions: ['edit', 'actions'],
      popUpAffectationsFace: false,
      produitInformation: null,
      constantes: {
        ECHEANCIER_CONTRAT,
      },
    };
  },
  components: {
    routerLinkBlank,
    popUpInfoAffectationsFace,
    BtnActionIcon,
    verticalDotsDropdown,
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
      parametresInterne: 'parametresInterne',
    }),
    modulePresseActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePresse.key);
    },
    columns() {
      return [
        'selected',
        'codeFace',
        'montantEcheanceHt',
        'montantEcheanceTtc',
        'referencePaiement',
        'bonAPayer',
        'commentaireLigneCommande',
        'validateur',
        'affectation',
        'edit',
        'actions',
      ];
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          codeFace: this.modulePresseActive ? this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.codeProduit') : this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.codeFace'),
          montantEcheanceHt: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.montantEcheanceHT'),
          montantEcheanceTtc: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.montantEcheanceTTC'),
          affectation: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.affectation'),
          commentaireLigneCommande: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.commentaireLigneCommande'),
          validateur: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.validateur'),
          referencePaiement: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.refPaiement'),
          bonAPayer: this.$i18n.t('listeEcheancesAPayer.tabEcheanceAPayerFace.bonAPayer'),
          actions: '',
          edit: '',
          selected: '',
        },
        columnsClasses: {
          commentaireLigneCommande: 'colonneCommentaire',
          selected: 'colonneSelected',
          edit: 'colonneSelected',
          actions: 'colonneSelected',
          bonAPayer: 'colonneWidth130px',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_POP_UP,
        texts: texts,
        sortable: this.columns.filter(c => !this.colonneSortExclusions.includes(c)),
      };
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
      loadParametreUtilisateur: 'loadParametreUtilisateur',
    }),
    getInformationDirection(affectation) {
      if (affectation.typeReference === TYPE_COMMANDE) {
        return { routeName: 'c.bodac.commandes', id: affectation.referenceId, message: affectation.codeReference };
      }
      if (affectation.typeReference === TYPE_DEVIS) {
        return { routeName: 'c.bodac.devis', id: affectation.referenceId, message: affectation.codeReference };
      }
      if (affectation.typeReference === TYPE_COMMANDE) {
        return { routeName: 'c.bodac.commandes', id: affectation.referenceId, message: affectation.codeReference };
      }
      if (affectation.sousTypeReference === CATEGORIE_REGROUPEMENT
        && affectation.typeReference === AFFECTATION_RESEAU) {
        return { routeName: 'c.bodac.catalogue-reseau', id: affectation.referenceId, message: affectation.libelle };
      }
      return { routeName: 'c.bodac.catalogue-reseauNumerique', id: affectation.referenceId, message: affectation.libelle };
    },
    getLibelleOfAffectation(affectation) {
      let libelle = affectation.libelle;
      if (affectation.typeReference === COMMANDER) {
        libelle += this.$i18n.t('listeProduitFace.infoAffectation.commande',
          { date: formatDate(affectation.dureeAffectation.dateFin) });
      }
      return libelle;
    },
    findLibelleValidateur(validateurIds) {
      let libelle = '';
      validateurIds.forEach((id) => {
        const user = this.parametre(PARAMETRES_KEYS.utilisateur)
          .find((utilisateur) => { return utilisateur.id === id; });
        if (user) libelle += nomPrenomGetter(user) + ', ';
      });
      return libelle.substring(0, libelle.length - 2);
    },
    getProduitRouting(row) {
      const produit = CATEGORIE_PRODUIT.find(cp => cp.code === row.typeProduit);
      if (!produit) return null;
      return {
        message: row.codeFace,
        to: {
          name: produit.routeDetail,
          params: { id: row.produitId },
        },
      };
    },
    saveInformationAndOpenPopUp(faceInformation) {
      this.produitInformation = faceInformation;
      this.popUpAffectationsFace = true;
    },
    dropdownItems(row) {
      return [
        {
          action: () => { this.$emit('replanifier', row); },
          libelle: this.$t('listeEcheancesAPayer.actions.replanifier'),
          permission: this.constantes.ECHEANCIER_CONTRAT.maj,
        },
      ];
    },
  },
};
</script>

<style>

</style>
